import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FaClock,
  FaMapMarkerAlt,
  FaPlane,
  FaRoute,
  FaTags,
  FaWhatsapp,
} from "react-icons/fa";
import ayiaNapaNissiBeachImage from "../assets/cyprus/ayia-napa-nissi-beach.webp";
import coralBayImage from "../assets/cyprus/coral-bay.webp";
import governorsBeachImage from "../assets/cyprus/governors-beach.webp";
import kakopetriaImage from "../assets/cyprus/kakopetria.webp";
import kolossiCastleImage from "../assets/cyprus/kolossi-castle.webp";
import kourionImage from "../assets/cyprus/kourion.webp";
import kykkosMonasteryImage from "../assets/cyprus/kykkos-monastery.webp";
import konnosBayImage from "../assets/cyprus/konnos-bay.webp";
import larnacaFinikoudesImage from "../assets/cyprus/larnaca-finikoudes.webp";
import ladysMileBeachImage from "../assets/cyprus/ladys-mile-beach.webp";
import lefkaraVillageImage from "../assets/cyprus/lefkara-village.webp";
import limassolMarinaImage from "../assets/cyprus/limassol-marina.webp";
import limassolOldTownImage from "../assets/cyprus/limassol-old-town.webp";
import nicosiaLedraStreetImage from "../assets/cyprus/nicosia-ledra-street.webp";
import omodosVillageImage from "../assets/cyprus/omodos-village.webp";
import paphosCastleImage from "../assets/cyprus/paphos-castle.webp";
import platresImage from "../assets/cyprus/platres.webp";
import protarasFigTreeBayImage from "../assets/cyprus/protaras-fig-tree-bay.webp";
import troodosMountainsImage from "../assets/cyprus/troodos-mountains.webp";
import { trackBookingClick, trackWhatsAppClick } from "../utils/analytics";

export default function CyprusPage() {
  const whatsappLink =
    "https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi.";

  const cardClass =
    "rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] shadow-xl shadow-black/25 transition-colors duration-300 hover:border-yellow-400/50 hover:shadow-yellow-400/10";
  const primaryButtonClass =
    "inline-flex items-center justify-center rounded-2xl bg-yellow-400 px-8 py-4 text-center font-extrabold text-black shadow-xl shadow-yellow-400/20 transition-all duration-200 hover:-translate-y-1 hover:bg-yellow-300 hover:shadow-yellow-400/30";
  const secondaryButtonClass =
    "inline-flex items-center justify-center gap-2 rounded-2xl bg-green-500 px-8 py-4 text-center font-extrabold text-white shadow-xl shadow-green-500/20 transition-all duration-200 hover:-translate-y-1 hover:bg-green-600";

  const places = [
    {
      name: "Limassol",
      description:
        "A vibrant coastal city with a modern marina, seafront promenade, old town restaurants, beaches, nightlife, and quick access to top Cyprus attractions.",
      image: limassolMarinaImage,
      link: "/",
    },
    {
      name: "Paphos",
      description:
        "One of the best places to visit in Cyprus for archaeology, sea views, resorts, the harbour, and UNESCO-listed ancient sites.",
      image: paphosCastleImage,
      link: "/paphos-airport-taxi",
    },
    {
      name: "Nicosia",
      description:
        "Cyprus' capital blends shopping, museums, old city walls, cafes, culture, and business travel in the heart of the island.",
      image: nicosiaLedraStreetImage,
    },
    {
      name: "Larnaca",
      description:
        "A popular coastal city for airport arrivals, Finikoudes promenade, beaches, restaurants, and relaxed seaside sightseeing.",
      image: larnacaFinikoudesImage,
      link: "/larnaca-airport-taxi",
    },
    {
      name: "Ayia Napa",
      description:
        "Famous for clear blue beaches, beach clubs, nightlife, boat trips, and some of the most photographed attractions in Cyprus.",
      image: ayiaNapaNissiBeachImage,
    },
    {
      name: "Protaras",
      description:
        "A family-friendly resort area known for Fig Tree Bay, coastal walks, calm water, villas, and beautiful sunrise views.",
      image: protarasFigTreeBayImage,
    },
    {
      name: "Troodos Mountains",
      description:
        "A scenic mountain escape with monasteries, wineries, forest roads, waterfalls, villages, and cooler summer temperatures.",
      image: troodosMountainsImage,
    },
    {
      name: "Omodos Village",
      description:
        "A traditional wine village with stone streets, tavernas, local shops, churches, and a classic Cyprus village atmosphere.",
      image: omodosVillageImage,
    },
  ];

  const beaches = [
    {
      name: "Nissi Beach",
      description:
        "A must-see Ayia Napa beach with turquoise water, soft sand, beach bars, and a lively summer atmosphere.",
      image: ayiaNapaNissiBeachImage,
    },
    {
      name: "Fig Tree Bay",
      description:
        "One of the most famous beaches in Protaras, loved for calm clear water, golden sand, and easy swimming.",
      image: protarasFigTreeBayImage,
    },
    {
      name: "Konnos Beach",
      description:
        "A sheltered bay between Ayia Napa and Protaras, ideal for scenic views, swimming, and relaxed beach days.",
      image: konnosBayImage,
    },
    {
      name: "Lady’s Mile Beach",
      description:
        "A long Limassol beach with shallow water, seafood restaurants, and easy access from the city.",
      image: ladysMileBeachImage,
    },
    {
      name: "Governor’s Beach",
      description:
        "A peaceful coastal spot with white rocks, dark sand, fish tavernas, and beautiful Mediterranean views.",
      image: governorsBeachImage,
    },
    {
      name: "Coral Bay",
      description:
        "A popular Paphos beach with a wide sandy bay, resort facilities, and calm water for families.",
      image: coralBayImage,
    },
  ];

  const cultureSites = [
    {
      name: "Kourion Archaeological Site",
      description:
        "An impressive ancient site near Limassol with a Greco-Roman theatre, mosaics, ruins, and panoramic sea views.",
      image: kourionImage,
    },
    {
      name: "Kolossi Castle",
      description:
        "A medieval landmark close to Limassol, known for its history, sugar production, and Commandaria wine heritage.",
      image: kolossiCastleImage,
    },
    {
      name: "Paphos Archaeological Park",
      description:
        "A UNESCO World Heritage attraction with Roman villas, mosaics, ancient ruins, and one of Cyprus' top cultural experiences.",
      image: paphosCastleImage,
    },
    {
      name: "Kykkos Monastery",
      description:
        "One of Cyprus' most important monasteries, located in the Troodos Mountains and popular for private sightseeing trips.",
      image: kykkosMonasteryImage,
    },
    {
      name: "Lefkara Village",
      description:
        "A charming traditional village famous for lace, silver, stone houses, narrow streets, and local craft shops.",
      image: lefkaraVillageImage,
    },
    {
      name: "Limassol Old Town",
      description:
        "A lively historic district with Limassol Castle, cafes, restaurants, boutique shops, and evening atmosphere.",
      image: limassolOldTownImage,
    },
  ];

  const villages = [
    {
      name: "Omodos Village",
      description:
        "A wine village with cobbled lanes, stone houses, traditional tavernas, and easy access from Limassol.",
      image: omodosVillageImage,
    },
    {
      name: "Lefkara Village",
      description:
        "Known for lace, silver, handmade crafts, and photogenic streets, Lefkara is a classic Cyprus day trip.",
      image: lefkaraVillageImage,
    },
    {
      name: "Kakopetria",
      description:
        "A mountain village in the Troodos foothills with river walks, old stone houses, and a cool forest setting.",
      image: kakopetriaImage,
    },
    {
      name: "Platres",
      description:
        "A peaceful Troodos resort village for waterfalls, mountain air, cafes, hotels, and nature escapes.",
      image: platresImage,
    },
  ];

  const faqs = [
    {
      question: "What are the best things to do in Cyprus?",
      answer:
        "The best things to do in Cyprus include visiting beaches such as Nissi Beach and Fig Tree Bay, exploring historic sites like Kourion and Paphos Archaeological Park, discovering mountain villages, enjoying Limassol Old Town, and booking private taxi tours for sightseeing across the island.",
    },
    {
      question: "What are the best places to visit from Limassol?",
      answer:
        "From Limassol, popular places to visit include Paphos, Nicosia, Larnaca, Ayia Napa, Protaras, Troodos Mountains, Omodos Village, Kourion Archaeological Site, Kolossi Castle, and Governor's Beach.",
    },
    {
      question: "Can I book a private taxi tour in Cyprus?",
      answer:
        "Yes. You can book private taxi tours in Cyprus from Limassol for beaches, villages, wineries, monasteries, historic sites, cities, airport transfers, and long-distance travel.",
    },
    {
      question: "How do I travel from Limassol to Ayia Napa or Protaras?",
      answer:
        "The easiest way to travel from Limassol to Ayia Napa or Protaras is by private taxi transfer. It gives you direct pickup, flexible timing, luggage space, and a comfortable door-to-door journey.",
    },
    {
      question: "Can I book airport transfers from Limassol?",
      answer:
        "Yes. Private taxi transfers are available from Limassol to Larnaca Airport and Paphos Airport, with pickup from hotels, apartments, offices, Limassol Marina, and nearby areas.",
    },
  ];

  const trustCards = [
    {
      title: "Airport Transfers",
      text: "Private taxi transfers to Larnaca Airport, Paphos Airport, hotels, homes, and resorts across Cyprus.",
      icon: FaPlane,
    },
    {
      title: "Fixed Prices",
      text: "Clear route pricing before you travel, with fast confirmation by phone or WhatsApp.",
      icon: FaTags,
    },
    {
      title: "24/7 Taxi Service",
      text: "Reliable taxi requests for early flights, late arrivals, sightseeing days, and long-distance trips.",
      icon: FaClock,
    },
  ];

  const popularCyprusRoutes = [
    {
      name: "Limassol to Larnaca Airport",
      description:
        "Direct private taxi transfer from Limassol to Larnaca Airport for departures, business trips, and holiday flights.",
      link: "/limassol-to-larnaca-airport-taxi",
    },
    {
      name: "Limassol to Paphos Airport",
      description:
        "Comfortable airport taxi from Limassol to Paphos Airport with door-to-door pickup and fixed price guidance.",
      link: "/limassol-to-paphos-airport-taxi",
    },
    {
      name: "Limassol to Nicosia",
      description:
        "Reliable city-to-city taxi service for meetings, appointments, shopping, and capital city visits.",
      link: "/limassol-to-nicosia",
    },
    {
      name: "Limassol to Ayia Napa",
      description:
        "Private transfer to Ayia Napa hotels, beaches, nightlife, and coastal attractions.",
      link: "/limassol-to-ayia-napa",
    },
    {
      name: "Limassol to Protaras",
      description:
        "Door-to-door taxi transfer to Protaras resorts, villas, Fig Tree Bay, and family-friendly beaches.",
      link: "/limassol-to-protaras",
    },
    {
      name: "Limassol to Troodos",
      description:
        "Scenic taxi transfer from Limassol to Troodos villages, wineries, monasteries, and mountain viewpoints.",
      link: "/limassol-to-troodos",
    },
  ];

  const travelTips = [
    {
      title: "Book airport transfers early",
      text: "Reserve your taxi before busy flight times, weekends, holidays, and summer travel days.",
      icon: FaClock,
    },
    {
      title: "Keep flight number ready",
      text: "Share your flight number when booking airport pickups or departures so timing is easier to confirm.",
      icon: FaPlane,
    },
    {
      title: "Confirm pickup location",
      text: "Send the exact hotel, villa, apartment, marina, or landmark so your driver can reach you quickly.",
      icon: FaMapMarkerAlt,
    },
    {
      title: "Use WhatsApp for fast communication",
      text: "WhatsApp is the quickest way to send route details, luggage information, and timing updates.",
      icon: FaWhatsapp,
    },
  ];

  const renderImageCard = (item, buttonText = "Plan Trip") => (
    <div
      key={item.name}
      className={`group overflow-hidden ${cardClass}`}
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={item.image}
          alt={item.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="text-2xl font-black text-white">
            {item.link ? (
              <Link to={item.link} className="transition-colors duration-200 hover:text-yellow-300">
                {item.name}
              </Link>
            ) : (
              item.name
            )}
          </h3>
        </div>
      </div>

      <div className="p-6">
        <p className="leading-7 text-neutral-400">
          {item.description}
        </p>

        <a
          href={whatsappLink}
          onClick={() => trackWhatsAppClick("cyprus_place_card")}
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-green-500 px-5 py-3 text-center font-bold text-white shadow-lg shadow-green-500/15 transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-600"
        >
          <FaWhatsapp />
          {buttonText}
        </a>
      </div>
    </div>
  );

  return (
    <main className="min-h-screen overflow-hidden bg-[#030712] text-white">
      <Helmet>
        <title>Things to Do in Cyprus | Cyprus Travel Guide & Taxi Tours</title>
        <meta
          name="description"
          content="Discover the best things to do in Cyprus, including beaches, historic sites, mountain villages and popular destinations. Book private taxi trips from Limassol across Cyprus."
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Things to Do in Cyprus | Cyprus Travel Guide & Taxi Tours"
        />
        <meta
          property="og:description"
          content="Discover the best things to do in Cyprus, including beaches, historic sites, mountain villages and popular destinations. Book private taxi trips from Limassol across Cyprus."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://limassolairporttaxi.com/cyprus" />
        <meta
          property="og:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Things to Do in Cyprus | Cyprus Travel Guide & Taxi Tours"
        />
        <meta
          name="twitter:description"
          content="Discover the best things to do in Cyprus, including beaches, historic sites, mountain villages and popular destinations. Book private taxi trips from Limassol across Cyprus."
        />
        <meta
          name="twitter:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <link rel="canonical" href="https://limassolairporttaxi.com/cyprus" />
      </Helmet>
      {/* HERO */}
      <section
        className="relative overflow-hidden bg-[#030712] px-6 pb-24 pt-40 md:pb-32 md:pt-48"
        style={{
          backgroundImage: `url(${limassolMarinaImage})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(250,204,21,0.1),transparent_34%),linear-gradient(115deg,rgba(0,0,0,0.5),rgba(0,0,0,0.35)_48%,rgba(0,0,0,0.5))]" />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#030712]/95 to-transparent" />

        <div className="relative mx-auto max-w-7xl">
          <div className="max-w-4xl">
            <p className="inline-flex rounded-full border border-yellow-400/30 bg-yellow-400/10 px-5 py-2 text-sm font-bold uppercase tracking-[0.18em] text-yellow-300 shadow-lg shadow-yellow-400/10">
              Cyprus Travel Guide
            </p>

            <h1 className="mt-6 text-5xl font-black leading-[1.02] tracking-tight text-white md:text-7xl">
              Things to Do in Cyprus
            </h1>

            <p className="mt-6 max-w-3xl text-lg leading-8 text-neutral-200">
              Discover the best beaches, historic sites, mountain villages,
              cities, and attractions across Cyprus with private taxi transfers
              from Limassol.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <a
                href="/#booking"
                onClick={() => trackBookingClick("cyprus_hero")}
                className={primaryButtonClass}
              >
                Book Taxi Now
              </a>

              <a
                href={whatsappLink}
                onClick={() => trackWhatsAppClick("cyprus_hero")}
                target="_blank"
                rel="noreferrer"
                className={secondaryButtonClass}
              >
                <FaWhatsapp />
                WhatsApp Booking
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
              Travel Cyprus with confidence
            </p>

            <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">
              Private taxi transfers for beaches, airports, cities, and villages
            </h2>

            <p className="mt-5 max-w-2xl text-lg leading-8 text-neutral-300">
              Cyprus is easy to explore when your transport is planned around your flight, hotel, luggage, and sightseeing schedule. Book a reliable taxi from Limassol for airport transfers, day trips, and long-distance routes across the island.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            {trustCards.map((card) => {
              const Icon = card.icon;

              return (
                <div key={card.title} className={`p-5 ${cardClass}`}>
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-yellow-400/10 text-yellow-300">
                    <Icon />
                  </div>
                  <h3 className="text-xl font-black text-white">
                    {card.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-neutral-400">
                    {card.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BEST PLACES */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="mb-12 max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
            Cyprus Attractions
          </p>

          <h2 className="mt-4 text-4xl font-black tracking-tight text-white">
            Best Places to Visit in Cyprus
          </h2>

          <p className="mt-4 leading-7 text-neutral-400">
            Plan your Cyprus travel guide around coastal cities, traditional
            villages, mountain scenery, beaches, historic landmarks, and private
            taxi transfers from Limassol.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {places.map((place) => renderImageCard(place, "Plan Trip"))}
        </div>
      </section>

      {/* BEACHES */}
      <section className="relative bg-[#09090b] px-6 py-20 md:py-24">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
              Beach Days
            </p>

            <h2 className="mt-4 text-4xl font-black tracking-tight text-white">
              Best Beaches in Cyprus
            </h2>

            <p className="mt-4 leading-7 text-neutral-400">
              Cyprus is known for clear Mediterranean water, sandy bays, beach
              restaurants, and scenic coastal drives that are easy to enjoy with
              a private taxi transfer.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {beaches.map((beach) => renderImageCard(beach, "Book Taxi"))}
          </div>
        </div>
      </section>

      {/* CULTURE */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="mb-12 max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
            History and Culture
          </p>

          <h2 className="mt-4 text-4xl font-black tracking-tight text-white">
            Historic Sites and Culture
          </h2>

          <p className="mt-4 leading-7 text-neutral-400">
            Explore ancient theatres, castles, monasteries, old towns, and
            villages that make Cyprus one of the most rewarding cultural
            destinations in the Mediterranean.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {cultureSites.map((site) => renderImageCard(site, "Plan Trip"))}
        </div>
      </section>

      {/* VILLAGES */}
      <section className="relative bg-[#09090b] px-6 py-20 md:py-24">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
              Mountain and Wine Routes
            </p>

            <h2 className="mt-4 text-4xl font-black tracking-tight text-white">
              Traditional Villages in Cyprus
            </h2>

            <p className="mt-4 leading-7 text-neutral-400">
              Add traditional villages to your Cyprus taxi tour for wineries,
              stone streets, mountain scenery, local food, and authentic island
              culture.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {villages.map((village) => renderImageCard(village, "Plan Trip"))}
          </div>
        </div>
      </section>

      {/* POPULAR ROUTES */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="mb-12 max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
            Popular Cyprus Routes
          </p>

          <h2 className="mt-4 text-4xl font-black tracking-tight text-white">
            Book private taxi routes from Limassol
          </h2>

          <p className="mt-4 leading-7 text-neutral-400">
            Choose one of the most requested Cyprus transfer routes for airports, cities, beach resorts, and mountain villages.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {popularCyprusRoutes.map((trip) => (
            <div
              key={trip.name}
              className={`p-6 ${cardClass}`}
            >
              <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-400/10 text-yellow-300">
                <FaRoute />
              </div>

              <h3 className="text-2xl font-black text-white">
                {trip.name}
              </h3>

              <p className="mt-4 leading-7 text-neutral-400">
                {trip.description}
              </p>

              <Link
                to={trip.link}
                className="mt-6 inline-flex rounded-2xl bg-yellow-400 px-5 py-3 text-center font-bold text-neutral-950 shadow-lg shadow-yellow-400/15 transition-all duration-200 hover:-translate-y-0.5 hover:bg-yellow-300"
              >
                View Route
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* TRAVEL TIPS */}
      <section className="relative bg-[#09090b] px-6 py-20 md:py-24">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
              Travel Tips in Cyprus
            </p>

            <h2 className="mt-4 text-4xl font-black tracking-tight text-white">
              Simple tips for smoother taxi travel
            </h2>

            <p className="mt-4 leading-7 text-neutral-400">
              A few small details make airport transfers, hotel pickups, and sightseeing trips easier to confirm.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {travelTips.map((tip) => {
              const Icon = tip.icon;

              return (
                <div key={tip.title} className={`p-6 ${cardClass}`}>
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-yellow-400/10 text-yellow-300">
                    <Icon />
                  </div>

                  <h3 className="text-xl font-black text-white">
                    {tip.title}
                  </h3>

                  <p className="mt-4 leading-7 text-neutral-400">
                    {tip.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative px-6 py-20 md:py-24">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
              Travel Questions
            </p>

            <h2 className="mt-4 text-4xl font-black tracking-tight text-white">
              FAQ
            </h2>
          </div>

          <div className="space-y-5">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.025] p-6 shadow-xl shadow-black/20"
              >
                <h3 className="text-xl font-black text-yellow-300">
                  {faq.question}
                </h3>

                <p className="mt-3 leading-7 text-neutral-400">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 py-20 md:py-24">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-yellow-400/20 via-white/[0.055] to-black/30 shadow-2xl shadow-yellow-400/10">
          <div className="grid md:grid-cols-2">
            <div className="relative min-h-[360px]">
              <img
                src={limassolMarinaImage}
                alt="Private taxi transfers in Cyprus"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            </div>

            <div className="p-8 md:p-12">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
                Private Taxi Transfers in Cyprus
              </p>

              <h2 className="mt-4 text-4xl font-black tracking-tight text-white">
                Need a Taxi While Visiting Cyprus?
              </h2>

              <p className="mt-5 text-lg leading-8 text-neutral-300">
                Book a reliable 24/7 taxi transfer with fixed prices and fast confirmation.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href="/#booking"
                  onClick={() => trackBookingClick("cyprus_final_cta")}
                  className={primaryButtonClass}
                >
                  Book Taxi Now
                </a>

                <a
                  href={whatsappLink}
                  onClick={() => trackWhatsAppClick("cyprus_final_cta")}
                  target="_blank"
                  rel="noreferrer"
                  className={secondaryButtonClass}
                >
                  <FaWhatsapp />
                  WhatsApp Booking
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
